import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../Services/AuthContext";
import {
  changePassword,
  deleteAccount,
  downloadExport,
  getSettings,
  resendVerification,
  startEmailChange,
  updateNotificationPreferences,
  updateProfile,
  type AccountSettings,
  type NotificationPreferences,
} from "../Services/AccountService";
import { Field, FormError, FormSuccess } from "../Components/Form/Field";
import { PasswordInput } from "../Components/Form/PasswordInput";
import { SubmitButton } from "../Components/Form/SubmitButton";
import { useSubmit } from "../Hooks/useSubmit";
import BlockedUsersSection from "../Components/Settings/BlockedUsersSection";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import "../Styles/Forms.css";
import "../Styles/Settings.css";

const MIN_AGE = 18;
const START_YEAR = 1900;

/**
 * Kontoinställningar.
 *
 * En sektion per sak användaren kan göra, var och en med eget submit-läge
 * och egna fel. Det är med flit inte ett stort formulär: den som byter
 * lösenord ska inte råka spara om sin bio, och ett fel i en sektion ska
 * inte blockera de andra.
 *
 * Allt hämtas i ett anrop (GET /me/settings) i stället för fyra, så sidan
 * inte kan hamna i ett läge där profilen laddats men notisinställningarna
 * inte.
 */
export default function Settings() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, logout, refresh } = useAuth();

  const [settings, setSettings] = useState<AccountSettings | null>(null);
  const [loadError, setLoadError] = useState<string>();

  const load = useCallback(async () => {
    try {
      setSettings(await getSettings());
    } catch {
      setLoadError("Could not load your settings. Reload the page to try again.");
    }
  }, []);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    load();
  }, [isAuthenticated, isLoading, load, navigate]);

  if (isLoading || (!settings && !loadError)) {
    return (
      <main className="settings-page">
        <p role="status">Loading your settings…</p>
      </main>
    );
  }

  if (!settings) {
    return (
      <main className="settings-page">
        <p role="alert">{loadError}</p>
      </main>
    );
  }

  return (
    <>
      <Header />

      <main className="settings-page">
        <h1>Settings</h1>

        <VerificationBanner settings={settings} onChanged={load} />

        <ProfileSection
          settings={settings}
          onSaved={async () => {
            await load();
            await refresh();
          }}
        />

        <PasswordSection settings={settings} />
        <EmailSection settings={settings} />
        <NotificationSection settings={settings} />
        <PrivacySection />
        <BlockedUsersSection />
        <DataSection />

        <DangerSection
          onDeleted={() => {
            logout();
            navigate("/", { replace: true });
          }}
        />
      </main>

      <Footer />
    </>
  );
}

/** Påminnelse om att adressen inte är bevisad, med ett sätt att fixa det. */
function VerificationBanner({
  settings,
  onChanged,
}: {
  settings: AccountSettings;
  onChanged: () => Promise<void>;
}) {
  const [sent, setSent] = useState(false);
  const { submit, pending, formError } = useSubmit(async () => {
    await resendVerification();
  });

  if (settings.user.emailVerified) return null;

  return (
    <section className="settings-section settings-banner" aria-labelledby="verify-heading">
      <h2 id="verify-heading">Confirm your email address</h2>
      <p>
        We sent a link to {settings.user.email}. Until it is confirmed we cannot
        reach you about your account.
      </p>

      {!settings.mailConfigured && (
        <p role="alert" className="form-error">
          Email is not set up on this server yet, so we cannot send it right now.
        </p>
      )}

      <FormError message={formError} />
      <FormSuccess message={sent ? "Sent. Check your inbox." : undefined} />

      <button
        className="login-button"
        type="button"
        disabled={pending || !settings.mailConfigured}
        aria-busy={pending}
        onClick={async () => {
          const result = await submit();
          if (result.ok) {
            setSent(true);
            await onChanged();
          }
        }}
      >
        {pending ? "Sending…" : "Send it again"}
      </button>
    </section>
  );
}

function ProfileSection({
  settings,
  onSaved,
}: {
  settings: AccountSettings;
  onSaved: () => Promise<void>;
}) {
  const [firstName, setFirstName] = useState(settings.user.firstName ?? "");
  const [lastName, setLastName] = useState(settings.user.lastName ?? "");
  const [bio, setBio] = useState(settings.user.bio ?? "");
  const [yob, setYob] = useState<number | "">(settings.user.yearOfBirth ?? "");
  const [saved, setSaved] = useState(false);

  const { submit, pending, fieldError, formError } = useSubmit(async () => {
    await updateProfile({
      firstName,
      lastName,
      bio,
      yearOfBirth: yob === "" ? null : yob,
    });
  });

  const maxYear = new Date().getFullYear() - MIN_AGE;
  const years = Array.from({ length: maxYear - START_YEAR + 1 }, (_, i) => maxYear - i);

  return (
    <section className="settings-section" aria-labelledby="profile-heading">
      <h2 id="profile-heading">Profile</h2>

      <form
        className="settings-form"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          setSaved(false);

          const result = await submit();
          if (result.ok) {
            setSaved(true);
            await onSaved();
          }
        }}
      >
        <Field label="First name" error={fieldError("firstName")}>
          {(props) => (
            <input
              {...props}
              className="form-input"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              disabled={pending}
            />
          )}
        </Field>

        <Field label="Last name" error={fieldError("lastName")}>
          {(props) => (
            <input
              {...props}
              className="form-input"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              disabled={pending}
            />
          )}
        </Field>

        <Field label="Year of birth" error={fieldError("yearOfBirth")}>
          {(props) => (
            <select
              {...props}
              className="form-input"
              value={yob === "" ? "" : String(yob)}
              onChange={(e) => setYob(e.target.value ? Number(e.target.value) : "")}
              disabled={pending}
            >
              <option value="">Prefer not to say</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Bio" error={fieldError("bio")} hint="Up to 280 characters.">
          {(props) => (
            <textarea
              {...props}
              className="form-input"
              rows={3}
              maxLength={280}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              disabled={pending}
            />
          )}
        </Field>

        <FormError message={formError} />
        <FormSuccess message={saved ? "Profile saved." : undefined} />

        <SubmitButton pending={pending} pendingLabel="Saving…">
          Save profile
        </SubmitButton>
      </form>
    </section>
  );
}

function PasswordSection({ settings }: { settings: AccountSettings }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState<string>();
  const [saved, setSaved] = useState(false);

  const { submit, pending, fieldError, formError } = useSubmit(async () => {
    await changePassword({ currentPassword, newPassword });
  });

  if (!settings.user.hasPassword) return null;

  return (
    <section className="settings-section" aria-labelledby="password-heading">
      <h2 id="password-heading">Password</h2>

      <form
        className="settings-form"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          setSaved(false);
          setMismatch(undefined);

          if (newPassword !== confirm) {
            setMismatch("The two passwords do not match.");
            return;
          }

          const result = await submit();
          if (result.ok) {
            setSaved(true);
            setCurrentPassword("");
            setNewPassword("");
            setConfirm("");
          }
        }}
      >
        <Field label="Current password" error={fieldError("currentPassword")} required>
          {(props) => (
            <PasswordInput
              {...props}
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              disabled={pending}
            />
          )}
        </Field>

        <Field
          label="New password"
          error={fieldError("newPassword")}
          hint="At least 8 characters, with a letter and a number."
          required
        >
          {(props) => (
            <PasswordInput
              {...props}
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              disabled={pending}
            />
          )}
        </Field>

        <Field label="Repeat new password" error={mismatch} required>
          {(props) => (
            <PasswordInput
              {...props}
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={pending}
            />
          )}
        </Field>

        <FormError message={formError} />
        <FormSuccess message={saved ? "Password changed." : undefined} />

        <SubmitButton pending={pending} pendingLabel="Saving…">
          Change password
        </SubmitButton>
      </form>
    </section>
  );
}

function EmailSection({ settings }: { settings: AccountSettings }) {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [pendingEmail, setPendingEmail] = useState<string>();

  const { submit, pending, fieldError, formError } = useSubmit(async () => {
    await startEmailChange({ newEmail, currentPassword });
  });

  return (
    <section className="settings-section" aria-labelledby="email-heading">
      <h2 id="email-heading">Email address</h2>

      <p>
        You sign in with <strong>{settings.user.email}</strong>
        {settings.user.emailVerified ? " (confirmed)." : " (not confirmed yet)."}
      </p>

      <form
        className="settings-form"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const result = await submit();

          if (result.ok) {
            setPendingEmail(newEmail);
            setNewEmail("");
            setCurrentPassword("");
          }
        }}
      >
        <Field label="New email address" error={fieldError("newEmail")} required>
          {(props) => (
            <input
              {...props}
              className="form-input"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoComplete="email"
              disabled={pending}
            />
          )}
        </Field>

        <Field label="Current password" error={fieldError("currentPassword")} required>
          {(props) => (
            <PasswordInput
              {...props}
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              disabled={pending}
            />
          )}
        </Field>

        <FormError message={formError} />
        <FormSuccess
          message={
            pendingEmail
              ? `We sent a confirmation link to ${pendingEmail}. Your address changes when you use it.`
              : undefined
          }
        />

        <SubmitButton pending={pending} pendingLabel="Sending…">
          Send confirmation link
        </SubmitButton>
      </form>
    </section>
  );
}

const PREFERENCE_LABELS: Array<{ key: keyof NotificationPreferences; label: string; hint: string }> = [
  { key: "checkIn", label: "Check-ins", hint: "When someone you follow checks in somewhere." },
  { key: "onMyWay", label: "On my way", hint: "When someone is heading to a place you are at." },
  { key: "follows", label: "Follows", hint: "Follow requests and accepted follows." },
  { key: "postLike", label: "Likes", hint: "When someone likes your post." },
  { key: "venueTrack", label: "Music at venues", hint: "When a new song plays where you or a friend is checked in." },
  {
    key: "goingOut",
    label: "Friends going out tonight",
    // Av från början, och den enda i listan som är det. Texten säger vad
    // man tackar ja till: en notis, inte en per vän som svarar.
    hint: "Off by default. One summary a night when friends are heading out — never one per friend.",
  },
  { key: "pushEnabled", label: "Push notifications", hint: "Turn off to keep notifications in the app only." },
];

function NotificationSection({ settings }: { settings: AccountSettings }) {
  const [preferences, setPreferences] = useState(settings.preferences);
  const [saved, setSaved] = useState(false);

  const { submit, pending, formError } = useSubmit(
    async (key: keyof NotificationPreferences, value: boolean) => {
      const res = await updateNotificationPreferences({ [key]: value });
      return res.preferences;
    }
  );

  return (
    <section className="settings-section" aria-labelledby="notifications-heading">
      <h2 id="notifications-heading">Notifications</h2>

      <div className="settings-toggles">
        {PREFERENCE_LABELS.map(({ key, label, hint }) => (
          <label className="form-checkbox" key={key}>
            <input
              type="checkbox"
              checked={preferences[key]}
              disabled={pending}
              onChange={async (e) => {
                const value = e.target.checked;
                setSaved(false);

                // Optimistiskt: rutan ska kännas direkt. Rullas tillbaka
                // om servern säger nej.
                setPreferences((prev) => ({ ...prev, [key]: value }));

                const result = await submit(key, value);

                if (result.ok) {
                  // Servern svarar med hela uppsättningen — den vinner
                  // över vår optimistiska gissning.
                  if (result.data) setPreferences(result.data);
                  setSaved(true);
                } else {
                  setPreferences((prev) => ({ ...prev, [key]: !value }));
                }
              }}
            />
            <span>
              {label}
              <span className="form-hint"> {hint}</span>
            </span>
          </label>
        ))}
      </div>

      <FormError message={formError} />
      <FormSuccess message={saved ? "Saved." : undefined} />
    </section>
  );
}

/**
 * Privacy-inställningar som redan har stöd i backend.
 *
 * Check-in-synligheten väljs per check-in (FOLLOWERS eller SELECTED) och
 * har ingen global inställning att spegla här — därför pekar sektionen
 * dit i stället för att visa ett reglage som inte gör något.
 */
function PrivacySection() {
  return (
    <section className="settings-section" aria-labelledby="privacy-heading">
      <h2 id="privacy-heading">Privacy</h2>

      <ul className="settings-list">
        <li>
          Beatspot never shares your live location. Only the place you explicitly
          check in to, and only with the people you choose at that moment.
        </li>
        <li>Check-ins expire on their own, and you can end one at any time.</li>
        <li>
          Who can see a check-in is chosen per check-in, on the venue card.
        </li>
        <li>
          <Link to="/privacy">Read the full privacy policy</Link>
        </li>
      </ul>
    </section>
  );
}

function DataSection() {
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);

  return (
    <section className="settings-section" aria-labelledby="data-heading">
      <h2 id="data-heading">Your data</h2>
      <p>Download everything linked to your account as a JSON file.</p>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      <button
        className="login-button"
        type="button"
        disabled={pending}
        aria-busy={pending}
        onClick={async () => {
          setError(undefined);
          setPending(true);

          try {
            await downloadExport();
          } catch {
            setError("Could not build your export. Try again shortly.");
          } finally {
            setPending(false);
          }
        }}
      >
        {pending ? "Preparing…" : "Download my data"}
      </button>
    </section>
  );
}

/**
 * Kontoradering.
 *
 * Två steg med flit: knappen öppnar ett formulär som kräver lösenordet och
 * att man skriver DELETE. Radering går inte att ångra, och en enda knapp
 * hade legat ett felklick från att ta bort någons check-ins och vänner.
 */
function DangerSection({ onDeleted }: { onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const { submit, pending, fieldError, formError } = useSubmit(async () => {
    await deleteAccount({ currentPassword });
  });

  const canDelete = confirmText.trim().toUpperCase() === "DELETE";

  return (
    <section className="settings-section settings-danger" aria-labelledby="danger-heading">
      <h2 id="danger-heading">Delete account</h2>
      <p>
        This removes your profile, check-ins, posts and follows. It cannot be
        undone. Download your data first if you want to keep it.
      </p>

      {!open ? (
        <button className="login-button" type="button" onClick={() => setOpen(true)}>
          Delete my account
        </button>
      ) : (
        <form
          className="settings-form"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            if (!canDelete) return;

            const result = await submit();
            if (result.ok) onDeleted();
          }}
        >
          <Field label="Current password" error={fieldError("currentPassword")} required>
            {(props) => (
              <PasswordInput
                {...props}
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
                disabled={pending}
              />
            )}
          </Field>

          <Field label="Type DELETE to confirm" required>
            {(props) => (
              <input
                {...props}
                className="form-input"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoComplete="off"
                disabled={pending}
              />
            )}
          </Field>

          <FormError message={formError} />

          <div className="settings-actions">
            <SubmitButton pending={pending} disabled={!canDelete} pendingLabel="Deleting…">
              Delete my account permanently
            </SubmitButton>

            <button
              className="login-button settings-cancel"
              type="button"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
